import type {
  BridgeMcpToolCallResult,
  BridgeMcpToolDefinition,
  BridgeMcpToolRegistry,
  BridgeMcpSharedContext,
} from './bridge-mcp-tool-types.js';
import {
  callForkSessionMcpTool,
  FORK_SESSION_MCP_TOOL_DEFINITIONS,
  isForkSessionMcpToolName,
} from './tools/session-history/index.js';
import {
  callIntegrationContentMcpTool,
  INTEGRATION_CONTENT_MCP_TOOL_DEFINITIONS,
  isIntegrationContentMcpToolName,
} from './tools/integration-content/index.js';

function requireCloud(
  shared: BridgeMcpSharedContext,
): { cloudApiBaseUrl: string; getCloudAccessToken: () => string } | null {
  const cloudApiBaseUrl = shared.cloudApiBaseUrl?.trim() ?? '';
  const getCloudAccessToken = shared.getCloudAccessToken;
  if (!cloudApiBaseUrl || !getCloudAccessToken) return null;
  return { cloudApiBaseUrl, getCloudAccessToken };
}

/**
 * Bridge-backed MCP tools. tools/list is session-agnostic; tools/call require sessionId in arguments.
 */
export function createBridgeMcpToolRegistry(shared: BridgeMcpSharedContext): BridgeMcpToolRegistry {
  const cloud = requireCloud(shared);

  return {
    listTools: async () => [...FORK_SESSION_MCP_TOOL_DEFINITIONS, ...INTEGRATION_CONTENT_MCP_TOOL_DEFINITIONS],
    callTool: async (name, args) => {
      if (isForkSessionMcpToolName(name)) {
        if (!cloud) {
          return {
            content: [{ type: 'text', text: 'Bridge MCP tools require cloud API credentials.' }],
            isError: true,
          };
        }
        const sessionId = typeof args.sessionId === 'string' ? args.sessionId.trim() : '';
        if (!sessionId) {
          return {
            content: [{ type: 'text', text: 'sessionId is required.' }],
            isError: true,
          };
        }
        return callForkSessionMcpTool({ ...shared, sessionId }, cloud, name, args);
      }
      if (isIntegrationContentMcpToolName(name)) {
        if (!cloud) {
          return {
            content: [{ type: 'text', text: 'Bridge MCP tools require cloud API credentials.' }],
            isError: true,
          };
        }
        const sessionId = typeof args.sessionId === 'string' ? args.sessionId.trim() : '';
        if (!sessionId) {
          return {
            content: [{ type: 'text', text: 'sessionId is required.' }],
            isError: true,
          };
        }
        return callIntegrationContentMcpTool({ ...shared, sessionId }, cloud, name, args);
      }
      throw new Error(`Unknown bridge MCP tool: ${name}`);
    },
  };
}
